import { useContext, useEffect, useReducer, useState } from "react";
import { useParams } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import {
  Box,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { BUDGET_STATUS_ACTIVE, BUDGET_STATUS_DRAFT, getBudgetYears } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { InsuranceIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import InsuranceCountyPracticeCard from "./components/InsuranceCountyPracticeCard";
import InsuranceCountyPracticeDataTable from "./components/InsuranceCountyPracticeDataTable";
import { InsuranceService } from "./InsuranceService";

export default function InsuranceCountyPracticeWorkspace() {
  const { globalState } = useContext(AppContext);
  const { insurance_scenario_id } = useParams();

  const { enqueueSnackbar } = useSnackbar();

  const CARD_GRID_KEY = "insurance_county_practice";
  const [workspaceMode, setWorkspaceMode] = useState(null);

  const { component: confirmDialogComponent } = useDialog();
  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  // --- --- ---

  const [isLoadingState, setIsLoadingState] = useState(true);
  const [state, dispatch] = useReducer(propUpdateReducer, {
    status: BUDGET_STATUS_DRAFT,
    is_active: true,
  });

  const handleChangeState = (event, value) => {
    value = event.target.value;

    if (state[event.target.name] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: event.target.name, value: value },
      });
    }
  };

  const handleChangeStateSelect = async (event) => {
    const { name, value } = event.target;
    if (state[name] === value) return;

    formStatusActions.setIsFormDirty(true, false);

    if (name !== "crop_year") {
      dispatch({
        type: "update",
        payload: { key: name, value: value },
      });
      return;
    }

    const previousYear = state?.crop_year;
    const currentScenarioId = state?.id;
    let nextStatus = state?.status || BUDGET_STATUS_DRAFT;

    try {
      if (globalState?.producer_token && previousYear && currentScenarioId) {
        const previousYearScenarios = await UsersApiHelper.users_selectObject(
          "InsuranceScenario",
          {
            producer_token: globalState.producer_token,
            crop_year: previousYear,
            is_active: true,
          },
          globalState
        );
        const remainingScenarios = (previousYearScenarios || []).filter(
          (scenario) => scenario.id !== currentScenarioId
        );
        if (remainingScenarios.length === 1 && remainingScenarios[0].status === BUDGET_STATUS_DRAFT) {
          await UsersApiHelper.updateInsuranceScenarios(
            {
              InsuranceScenarios: [
                {
                  ...remainingScenarios[0],
                  producer_token: globalState.producer_token,
                  status: BUDGET_STATUS_ACTIVE,
                },
              ],
            },
            globalState
          );
        }
      }

      if (globalState?.producer_token && value && currentScenarioId) {
        const nextYearScenarios = await UsersApiHelper.users_selectObject(
          "InsuranceScenario",
          {
            producer_token: globalState.producer_token,
            crop_year: value,
            is_active: true,
          },
          globalState
        );
        const otherScenarios = (nextYearScenarios || []).filter((scenario) => scenario.id !== currentScenarioId);
        nextStatus = otherScenarios.length === 0 ? BUDGET_STATUS_ACTIVE : BUDGET_STATUS_DRAFT;
      }
    } catch (err) {
      handleError(err);
    }

    dispatch({
      type: "updateMany",
      payload: {
        crop_year: value,
        status: nextStatus,
      },
    });
  };

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadInsuranceScenarioState = async (_insurance_scenario_id) => {
    const producerFilter = {
      producer_token: globalState.producer_token,
      id: _insurance_scenario_id,
      is_active: true,
    };
    const _insuranceScenarios = await UsersApiHelper.users_selectObject(
      "InsuranceScenario",
      producerFilter,
      globalState
    );
    if (_insuranceScenarios?.length) {
      dispatch({
        type: "updateMany",
        payload: _insuranceScenarios[0],
      });
    }

    setIsLoadingState(false);
  };
  const [insuranceScenario, setInsuranceScenario] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadInsuranceScenario = async (_insurance_scenario_id, _crop_year) => {
    setInsuranceScenario(null);
    setIsLoading(true);
    try {
      // setIsLoading(true);
      if (state?.id !== 0) {
        const _InsuranceService = new InsuranceService({
          crop_year: _crop_year,
          insuranceScenarios: [{ id: state?.id }],
          onError: handleError,
          apiConfig: globalState,
        });

        const _insuranceScenarioPopulated = await _InsuranceService.actions.loadInsuranceScenario();

        if (_insuranceScenarioPopulated?.length) {
          setInsuranceScenario(_insuranceScenarioPopulated[0]);
        } else {
          setInsuranceScenario({});
        }

        const _calculatePremiumsResponseInsuranceScenario = await _InsuranceService.actions.calculatePremiums(
          _insuranceScenarioPopulated[0]
        );
        if (_calculatePremiumsResponseInsuranceScenario) {
          setInsuranceScenario(_calculatePremiumsResponseInsuranceScenario);
        } else {
          setInsuranceScenario({});
        }
      } else {
        setIsLoadingState(false);
      }
    } catch (err) {
      setIsLoadingState(false);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isValid() && globalState.producer_token) {
      formStatusActions.setIsFormDirty(true, true);

      state.producer_token = globalState?.producer_token;
      UsersApiHelper.updateInsuranceScenarios({ InsuranceScenarios: [state] }, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
            preventDuplicate: true,
            variant: "error",
          });
        });
    }
  }, [state, globalState.producer_token]);
  useEffect(() => {
    if (globalState?.producer_token) {
      if (String(insurance_scenario_id) === "0") {
        if (!state?.id) {
          dispatch({
            type: "update",
            payload: { key: "id", value: uuidv4() },
          });
        }
        setIsLoadingState(false);
      } else {
        loadInsuranceScenarioState(insurance_scenario_id);
        dispatch({
          type: "update",
          payload: { key: "id", value: insurance_scenario_id },
        });
      }
    }
  }, [globalState?.producer_token, insurance_scenario_id, state?.crop_year]);

  useEffect(() => {
    if (globalState?.producer_token) {
      if (String(insurance_scenario_id) === "0") {
        if (!state?.id) {
          dispatch({
            type: "update",
            payload: { key: "id", value: uuidv4() },
          });
          setIsLoading(false);
        }
        setIsLoading(false);
      } else if (state?.crop_year) {
        loadInsuranceScenario(insurance_scenario_id, state?.crop_year);
        dispatch({
          type: "update",
          payload: { key: "id", value: insurance_scenario_id },
        });
      }
    }
  }, [globalState?.producer_token, insurance_scenario_id, state?.crop_year]);

  useEffect(() => {
    if (state?.id && state?.crop_year) {
      loadInsuranceScenario(state?.id, state?.crop_year);
    } else {
      if (String(insurance_scenario_id) === "0") {
        setIsLoading(false);
      }
    }
  }, [state?.id, state?.crop_year, globalState, insurance_scenario_id]);
  const isValid = () => {
    return Boolean(state?.id && state?.id !== 0 && state?.name && state?.crop_year);
  };
  useBlockerPrompt(!isValid(), "InsuranceCountyPracticeWorkspace");

  // --- --- ---

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  return (
    <>
      <WorkspaceHeader
        title={state?.name || "Scenario"}
        category={{
          title: "Insurance",
          icon: <InsuranceIcon />,
        }}
        breadCrumbs={[
          {
            title: "Scenarios",
            path: "/insurance",
          },
        ]}
      />

      <Stack direction="row" spacing={2} className="headerWithMenuButton headerWithMenuButtonAlignEnd">
        <Typography variant="h1">Insurance Scenario</Typography>
        {formStatusComponent}
      </Stack>
      <Grid container spacing={1}>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth={true}>
            <StatefulControlledTextField
              label="Scenario Name"
              name="name"
              isLoading={isLoadingState}
              isRequired={true}
              defaultValue={state?.name || ""}
              variant="outlined"
              onChange={handleChangeState}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth>
            <SkeletonWrapper variant="textfield" isLoading={isLoadingState}>
              <InputLabel id="selectYearLabel">Year</InputLabel>
              <Select
                labelId="selectYearLabel"
                value={state?.crop_year || ""}
                name={"crop_year"}
                onChange={handleChangeStateSelect}
                label={"Year"}
              >
                {getBudgetYears()?.map((currentItem, index) => {
                  return (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  );
                })}
              </Select>
            </SkeletonWrapper>
          </FormControl>
        </Grid>
      </Grid>

      <Divider />

      <WorkspacePageHeader
        title="Crop Plans"
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        isLoading={isLoading}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <>
          {Boolean(
            insuranceScenario?.budgetCountyPractices?.length &&
            insuranceScenario?.budgetCountyPractices?.length > 1 &&
            insuranceScenario?.totals?.counties?.length >= 1
          ) &&
            insuranceScenario?.totals?.counties?.map((currentCounty, countyIndex) => {
              const _countyScenarios = insuranceScenario?.budgetCountyPractices?.filter(
                (ins) => ins.state_name === currentCounty.state_name && ins.county_name === currentCounty.county_name
              );

              return (
                <div key={countyIndex}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {currentCounty?.county_name + ", " + currentCounty?.state_name}
                  </Typography>
                  <Box className="cardContainer">
                    {_countyScenarios.map((currentCounty, index) => (
                      <InsuranceCountyPracticeCard
                        key={`${countyIndex}/${index}`}
                        insuranceScenario={insuranceScenario}
                        budgetCountyPractice={currentCounty}
                        isLoading={isLoading}
                      />
                    ))}
                  </Box>
                  <br />
                </div>
              );
            })}

          {Boolean(
            insuranceScenario?.budgetCountyPractices?.length &&
            (insuranceScenario?.budgetCountyPractices?.length === 1 ||
              (insuranceScenario?.totals?.counties?.length || 0) < 1)
          ) && (
            <Box className="cardContainer">
              {insuranceScenario?.budgetCountyPractices.map((currentCounty, index) => (
                <InsuranceCountyPracticeCard
                  key={index}
                  insuranceScenario={insuranceScenario}
                  budgetCountyPractice={currentCounty}
                  isLoading={isLoading}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <>
          {Boolean(
            insuranceScenario?.budgetCountyPractices?.length &&
            insuranceScenario?.budgetCountyPractices?.length > 1 &&
            insuranceScenario?.totals?.counties?.length >= 1
          ) && (
            <InsuranceCountyPracticeDataTable
              insuranceScenario={insuranceScenario}
              isLoading={isLoading}
              isFilter={true}
            />
          )}

          {Boolean(
            insuranceScenario?.budgetCountyPractices?.length &&
            (insuranceScenario?.budgetCountyPractices?.length === 1 ||
              (insuranceScenario?.totals?.counties?.length || 0) < 1)
          ) && (
            <InsuranceCountyPracticeDataTable
              insuranceScenario={insuranceScenario}
              isLoading={isLoading}
              isFilter={false}
            />
          )}
        </>
      )}

      {!isValid() && !insuranceScenario?.budgetCountyPractices?.length && (
        <Box className="cardContainer">
          <EmptyCard
            text="No crop plans listed"
            secondaryText="Provide a name and select a year"
            isLoading={isLoading}
          />
        </Box>
      )}

      {isValid() && !insuranceScenario?.budgetCountyPractices?.length && (
        <Box className="cardContainer">
          <EmptyCard
            text="No crop plans listed"
            secondaryText="First, setup your crop plans in the farm vault & create a budget"
            isLoading={isLoading}
          />
        </Box>
      )}

      <WorkspaceActionBar content={<></>} />

      {confirmDialogComponent}
    </>
  );
}
